/**
 * Tela de verificação 2FA durante o login
 * Mostrada quando a API retorna requires_2fa: true
 */

import { useState } from 'preact/hooks';
import { api } from '../api/client';
import { fetchMe } from '../stores/auth.store';
import { route } from 'preact-router';
import { T } from '../styles/tokens';
import { Button } from '../components/ui';

interface TwoFactorVerifyProps {
  tempToken: string;
}

export function TwoFactorVerify({ tempToken }: TwoFactorVerifyProps) {
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  async function handleVerify() {
    setError('');
    setVerifying(true);
    try {
      if (mode === 'totp') {
        await api.post('/auth/2fa/verify', { token: code, tempToken });
      } else {
        await api.post('/auth/2fa/backup', { code: backupCode.toUpperCase().trim(), tempToken });
      }
      await fetchMe();
      route('/chat');
    } catch (e: any) {
      setError(e.message || 'Código inválido. Tente novamente.');
    }
    setVerifying(false);
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', padding: 24,
    }}>
      <div style={{
        maxWidth: 420, width: '100%', textAlign: 'center' as any,
        background: T.bgCard, borderRadius: 16, padding: 32,
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 8 }}>
          Verificação em Dois Fatores
        </h2>
        <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 24 }}>
          {mode === 'totp'
            ? 'Digite o código de 6 dígitos do seu app autenticador.'
            : 'Digite um dos seus códigos de backup.'}
        </p>

        {error && (
          <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
            color: '#EF4444', fontSize: 13, fontWeight: 600,
          }}>
            {error}
          </div>
        )}

        {mode === 'totp' ? (
          <input
            type="text"
            maxLength={6}
            value={code}
            autoFocus
            onInput={(e) => {
              const val = (e.target as HTMLInputElement).value.replace(/\D/g, '');
              setCode(val);
              if (val.length === 6) {
                setTimeout(() => handleVerify(), 100);
              }
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) handleVerify(); }}
            placeholder="000000"
            style={{
              width: '100%', padding: '16px', borderRadius: 12, fontSize: 28, textAlign: 'center' as any,
              letterSpacing: 12, fontFamily: 'monospace', fontWeight: 800,
              background: T.bg, border: '1px solid #374151', color: T.text, outline: 'none',
              boxSizing: 'border-box' as any, marginBottom: 16,
            }}
          />
        ) : (
          <input
            type="text"
            maxLength={8}
            value={backupCode}
            autoFocus
            onInput={(e) => setBackupCode((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && backupCode.length >= 6) handleVerify(); }}
            placeholder="ABCD1234"
            style={{
              width: '100%', padding: '16px', borderRadius: 12, fontSize: 20, textAlign: 'center' as any,
              letterSpacing: 6, fontFamily: 'monospace', fontWeight: 700,
              background: T.bg, border: '1px solid #374151', color: T.text, outline: 'none',
              boxSizing: 'border-box' as any, marginBottom: 16, textTransform: 'uppercase' as any,
            }}
          />
        )}

        <Button
          variant="primary"
          onClick={handleVerify}
          disabled={verifying || (mode === 'totp' ? code.length !== 6 : backupCode.length < 6)}
          style={{ width: '100%', padding: '12px', fontSize: 15, fontWeight: 700, marginBottom: 16 }}
        >
          {verifying ? 'Verificando...' : 'Verificar'}
        </Button>

        <button
          onClick={() => { setMode(mode === 'totp' ? 'backup' : 'totp'); setError(''); setCode(''); setBackupCode(''); }}
          style={{
            background: 'none', border: 'none', color: T.primary,
            fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          {mode === 'totp' ? 'Usar código de backup' : 'Usar código do app autenticador'}
        </button>
      </div>
    </div>
  );
}
